import type { BusyReason, ChatEvent, DoneStatus, Question, ServerEvent } from '@harness/shared/protocol';

export type TimelineItem =
  | { kind: 'prompt'; text: string; author: string | null }
  | { kind: 'text'; text: string }
  | { kind: 'tool'; tool: string; target: string | null }
  | { kind: 'files'; paths: string[] }
  | { kind: 'question'; requestId: string; questions: Question[]; closed: boolean }
  | { kind: 'approval'; requestId: string; tool: string; input: unknown; allowTurn: boolean; closed: boolean }
  | { kind: 'connect'; requestId: string; integration: string; url: string; closed: boolean }
  | { kind: 'end'; status: Exclude<DoneStatus, 'ok'>; detail: string | null }
  | { kind: 'error' };

export interface Notice {
  reason: BusyReason;
}

export interface ChatState {
  chatId: string | null;
  title: string | null;
  items: TimelineItem[];
  running: boolean;
  notice: Notice | null;
  presence: { name: string; chatTitle: string; automation?: boolean }[];
  connected: boolean;
}

export type Action =
  | { type: 'server'; event: ServerEvent }
  | { type: 'history'; chatId: string; title: string; events: ServerEvent[] }
  | { type: 'new-chat' }
  | { type: 'sent-prompt' }
  | { type: 'connection'; connected: boolean };

export const initialState: ChatState = { chatId: null, title: null, items: [], running: false, notice: null, presence: [], connected: false };

type RequestItem = Extract<TimelineItem, { requestId: string }>;

function isRequest(item: TimelineItem): item is RequestItem {
  return item.kind === 'question' || item.kind === 'approval' || item.kind === 'connect';
}

function hasRequest(items: TimelineItem[], requestId: string): boolean {
  return items.some((item) => isRequest(item) && item.requestId === requestId);
}

function closeAll(items: TimelineItem[]): TimelineItem[] {
  return items.map((item) => (isRequest(item) && !item.closed ? { ...item, closed: true } : item));
}

function isChatEvent(event: ServerEvent): event is ChatEvent {
  return 'chatId' in event && event.type !== 'busy';
}

/** Applies one event of the current chat. */
function applyChatEvent(state: ChatState, event: ChatEvent): ChatState {
  const add = (item: TimelineItem): ChatState => ({ ...state, items: [...state.items, item] });
  switch (event.type) {
    case 'chat':
      return state;
    case 'prompt':
      return { ...add({ kind: 'prompt', text: event.text, author: event.author ?? null }), running: true };
    case 'text':
      return add({ kind: 'text', text: event.text });
    case 'tool':
      return add({ kind: 'tool', tool: event.tool, target: event.target });
    case 'files':
      return add({ kind: 'files', paths: event.paths });
    case 'question':
      return hasRequest(state.items, event.requestId) ? state : add({ kind: 'question', requestId: event.requestId, questions: event.questions, closed: false });
    case 'approval':
      return hasRequest(state.items, event.requestId)
        ? state
        : add({ kind: 'approval', requestId: event.requestId, tool: event.tool, input: event.input, allowTurn: event.allowTurn, closed: false });
    case 'connect':
    case 'pigment-connect': {
      const integration = event.type === 'connect' ? event.integration : 'pigment';
      return hasRequest(state.items, event.requestId) ? state : add({ kind: 'connect', requestId: event.requestId, integration, url: event.url, closed: false });
    }
    case 'request-closed':
      return {
        ...state,
        items: state.items.map((item) => (isRequest(item) && item.requestId === event.requestId ? { ...item, closed: true } : item)),
      };
    case 'done': {
      const items = closeAll(state.items);
      return {
        ...state,
        running: false,
        items: event.status === 'ok' ? items : [...items, { kind: 'end', status: event.status, detail: event.detail }],
      };
    }
  }
}

function applyServerEvent(state: ChatState, event: ServerEvent): ChatState {
  switch (event.type) {
    case 'presence':
      return { ...state, presence: event.active };
    case 'integrations':
    case 'automations':
    case 'data':
    case 'skills':
      return state;
    case 'error':
      return { ...state, items: [...state.items, { kind: 'error' }] };
    case 'chat': {
      const same = state.chatId === event.chatId;
      return { ...state, chatId: event.chatId, title: event.title, running: true, notice: null, items: same ? state.items : [] };
    }
    case 'busy':
      // A busy reply to a new prompt has no chat yet; it belongs to whatever is open.
      return event.chatId === null || event.chatId === state.chatId ? { ...state, notice: { reason: event.reason } } : state;
    default:
      return event.chatId === state.chatId ? applyChatEvent(state, event) : state;
  }
}

export function reduce(state: ChatState, action: Action): ChatState {
  switch (action.type) {
    case 'server':
      return applyServerEvent(state, action.event);
    case 'history': {
      const base: ChatState = { ...state, chatId: action.chatId, title: action.title, items: [], running: false, notice: null };
      // The history ends with the open requests the worker still waits for; a running answer has no 'done' yet.
      return action.events.reduce((current, event) => (isChatEvent(event) && event.chatId === action.chatId ? applyChatEvent(current, event) : current), base);
    }
    case 'new-chat':
      return { ...state, chatId: null, title: null, items: [], running: false, notice: null };
    case 'sent-prompt':
      return { ...state, running: true, notice: null };
    case 'connection':
      return { ...state, connected: action.connected };
  }
}
